import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { colorsApi } from "../api/colors";
import { productsApi, type DraftVariantBody } from "../api/products";
import type { ProcessingJobStatus } from "../api/types";
import { uploadsApi } from "../api/uploads";
import { Shell } from "../app/Shell";
import { useAuth } from "../auth/AuthProvider";
import { CAP } from "../auth/capabilities";
import type { PaletteColor } from "../catalog/colors";
import { lensColorForKey } from "../catalog/lensColors";
import { familyForCategory, type ProductFamily } from "../catalog/taxonomy";
import { ErrorNotice, StatusBadge, formatMoney } from "../components/ui";
import { DetectionReview, type ColorAssignments } from "../import/DetectionReview";
import { filesFromDirectoryInput, filesFromDrop } from "../import/folderPicker";
import {
  detailProblems,
  emptyDetails,
  type GlassesDetails,
} from "../import/glassesDetails";
import {
  parseGlassesFolder,
  resolveGlassesFolder,
  type GlassesFolderParse,
  type RoleOverrides,
} from "../import/glassesFolderParser";
import {
  PHASE_LABELS,
  useGlassesImport,
  type ImportInput,
  type ImportPhase,
} from "../import/useGlassesImport";
import {
  resolveModelCandidate,
  type ModelCandidate,
} from "../import/modelCandidate";
import { SIZE_LABELS, type SizeOption } from "../catalog/taxonomy";
import { ConfirmDialog } from "./ConfirmDialog";
import { HistoryPanel } from "./HistoryPanel";
import { ImagesPanel } from "./ImagesPanel";
import { LifecycleActions } from "./LifecycleActions";
import { MetadataForm } from "./MetadataForm";
import { ValidationPanel } from "./ValidationPanel";
import { VariantsPanel } from "./VariantsPanel";
import {
  ClassificationStep,
  EMPTY_CLASSIFICATION,
  isClassificationComplete,
  type Classification,
} from "./add/ClassificationStep";
import { ImportRunPanel } from "./add/ImportRunPanel";
import { ImportSummary } from "./add/ImportSummary";
import {
  NonGlassesDetailsStep,
  emptyNonGlassesDetails,
  nonGlassesProblems,
  parseStock,
  type NonGlassesDetails,
} from "./add/NonGlassesDetailsStep";
import { ProductDetailsStep, type ResolvedColor } from "./add/ProductDetailsStep";
import {
  ProductPhotosStep,
  photoProblems,
  photosInSlotOrder,
  type PhotoSelection,
} from "./add/ProductPhotosStep";
import { INTAKE_PHASE_LABELS, useProductIntake } from "./add/useProductIntake";
import { parseMoneyToMinor } from "./money";
import { useProductDetail, useProductRefresh } from "./useProduct";
import "./products.css";

/**
 * The one place a product is worked on, whichever way an administrator arrived.
 *
 * There used to be three: an Add page that owned the whole intake, an Edit page
 * that owned a second copy of the same questions with its own save, validate
 * and publish, and this workspace. Three copies of "what is a product" drift —
 * the edit screen offered frame shape as a free-text box while the add screen
 * offered the shop's thirteen, so the same product could be described two ways
 * depending on which door was used. Now there is one door with three modes:
 *
 *   create   nothing exists yet; the intake runs and ends at a real product
 *   edit     the product exists; its draft is edited and published
 *   resume   the product exists but its import did not finish; it is picked up
 *
 * `resume` is deliberately not a route and not a prop. It is a fact about the
 * product read back from the server, because the alternative — trusting a URL
 * to say whether an import is unfinished — is how a second upload gets started
 * for a job that was merely slow.
 */
export type WorkspaceMode = "create" | "edit";

type Tab = "details" | "colors" | "images" | "validation" | "history";

/** Lifecycle actions the Products List may deep-link with ?action=. */
const DEEP_LINKABLE = [
  "open_draft",
  "publish",
  "publish_changes",
  "archive",
] as const;

type DeepLinkable = (typeof DEEP_LINKABLE)[number];

function deepLinkedAction(raw: string | null): DeepLinkable | null {
  return DEEP_LINKABLE.includes(raw as DeepLinkable) ? (raw as DeepLinkable) : null;
}

export function ProductWorkspacePage({ mode = "edit" }: { mode?: WorkspaceMode }) {
  if (mode === "create") return <CreateWorkspace />;
  return <ExistingProductWorkspace />;
}

/* ------------------------------------------------------------------ create */

/**
 * Adding a product, as one page that unlocks downward.
 *
 * The administrator answers what kind of product it is, then hands over one
 * folder, then confirms what was found in it, then says what the shop needs to
 * know, then checks the whole thing once before anything is sent. Each section
 * appears only once the one above it is settled, so there is never a form with
 * half its questions greyed out for reasons that have to be worked out.
 *
 * Nothing before Start Import touches the backend except reading the palette:
 * the folder is read locally, judged locally, and shown back. Creation, upload
 * and processing begin on one deliberate press and are shown as they happen by
 * useGlassesImport, which owns that orchestration and is not reimplemented here.
 */
function CreateWorkspace() {
  const [classification, setClassification] =
    useState<Classification>(EMPTY_CLASSIFICATION);

  // The shop's palette, read when the page opens rather than when the frame
  // steps appear. It is held here for the same reason it always was: a colour
  // added mid-import must be immediately available to every other colour card,
  // and that is a property of the page, not of the section that renders last.
  const palette = useQuery({
    queryKey: ["colors"],
    queryFn: ({ signal }) => colorsApi.list(signal),
    staleTime: 60_000,
  });

  return (
    <Shell title="Add Product">
      <ol className="steps">
        <li className="steps__item">
          <h2 className="steps__title">1. What are you adding?</h2>
          <ClassificationStep value={classification} onChange={setClassification} />
        </li>

        {/* One workspace, three families. What the family changes is which
            sections follow — never which page the administrator is on, and
            never which editor they end up in afterwards. A frame's intake is a
            folder the pipeline composes views from; the other two are an
            ordinary product with ordinary photographs, and pushing either
            through the other's flow would be describing it as something it is
            not. */}
        {classification.family === "glasses" ? (
          <GlassesIntakeSteps
            classification={classification}
            palette={palette.data ?? []}
          />
        ) : null}

        {classification.family === "contact_lenses" ||
        classification.family === "accessories" ? (
          <NonGlassesIntakeSteps family={classification.family} />
        ) : null}
      </ol>
    </Shell>
  );
}

/**
 * Adding a product that is photographed the ordinary way.
 *
 * Draft-first, and it has to be: the family decides how a photograph is
 * treated, the family is read off the product, so the product exists before the
 * first photograph is sent. That is the canonical backend path for these two
 * families — POST /v1/admin/products with no upload_batch_id — and nothing here
 * fabricates a batch to imitate the frame intake or asks the folder parser to
 * interpret a file it was never given.
 */
function NonGlassesIntakeSteps({
  family,
}: {
  family: Exclude<ProductFamily, "glasses">;
}) {
  const [details, setDetails] = useState<NonGlassesDetails>(emptyNonGlassesDetails);
  // One selection per variant, in the order the variants are created. A product
  // with one variant has one group and behaves exactly as it always has; a
  // genuine multi-variant accessory photographs each colour separately, because
  // a photograph of the silver chain is not a photograph of the gold one.
  const [photos, setPhotos] = useState<PhotoSelection[]>([{}]);
  const [showProblems, setShowProblems] = useState(false);
  const intake = useProductIntake();

  const variants = variantsForCreate(details, family);
  // One photograph group per colour, and always at least one: a lens has no
  // variant at all until its colour is chosen, and the photograph slots must
  // not vanish while the administrator is still filling the form above them.
  // Named per colour only when there is a real choice to name.
  const groups: { name?: string }[] =
    variants.length > 1 ? variants : [variants[0] ?? {}];

  const selectionFor = (index: number): PhotoSelection => photos[index] ?? {};
  const setSelection = (index: number, next: PhotoSelection) =>
    setPhotos((current) => {
      const copy = [...current];
      while (copy.length <= index) copy.push({});
      copy[index] = next;
      return copy;
    });

  const detailIssues = nonGlassesProblems(details, family);
  // Every colour that will be created needs its own photo_1: the backend now
  // refuses to publish a colour with no image of its own, and a colour whose
  // photograph is missing here is that refusal waiting to happen.
  const photoIssues = groups.flatMap((variant, index) =>
    photoProblems(selectionFor(index)).map((problem) =>
      groups.length > 1 ? `${variant.name || `Variant ${index + 1}`}: ${problem}` : problem,
    ),
  );
  const problems = [...detailIssues, ...photoIssues];
  const running = intake.state.phase !== "idle" && intake.state.phase !== "failed";
  const done = intake.state.phase === "complete";

  const submit = () => {
    if (problems.length > 0) {
      setShowProblems(true);
      return;
    }
    // Each photograph carries the colour it belongs to. Nothing is copied
    // between colours to fill a gap — that would put one colour's picture
    // under another colour's name.
    const chosen = groups.flatMap((_variant, index) =>
      photosInSlotOrder(selectionFor(index)).map((photo) => ({
        ...photo,
        variantIndex: index,
      })),
    );
    void intake.start({
      name: details.name.trim(),
      productCode: details.productCode.trim(),
      description: details.description.trim(),
      category: family,
      basePrice: parseMoneyToMinor(details.basePrice) ?? 0,
      salePrice: details.salePrice.trim()
        ? parseMoneyToMinor(details.salePrice)
        : null,
      currency: "ILS",
      variants,
      photos: chosen,
    });
  };

  return (
    <>
      <li className="steps__item">
        <h2 className="steps__title">2. Product information</h2>
        <NonGlassesDetailsStep
          family={family}
          details={details}
          problems={detailIssues}
          showProblems={showProblems}
          onChange={setDetails}
        />
      </li>

      <li className="steps__item">
        <h2 className="steps__title">3. Photographs</h2>
        {groups.length > 1 ? (
          <p className="steps__hint">
            Each colour is photographed separately — a photograph of one is not
            a photograph of another, and a colour published without its own
            photograph cannot be shown.
          </p>
        ) : null}
        {groups.map((variant, index) => (
          <ProductPhotosStep
            key={index}
            value={selectionFor(index)}
            onChange={(next) => setSelection(index, next)}
            disabled={running}
            variantName={
              groups.length > 1
                ? variant.name || `Variant ${index + 1}`
                : undefined
            }
            groupKey={groups.length > 1 ? `v${index}` : undefined}
          />
        ))}
        {showProblems && photoIssues.length > 0 ? (
          <div className="notice notice--warning" role="status" data-testid="photo-problems">
            <ul>
              {photoIssues.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </li>

      {!done ? (
        <li className="steps__item">
          <h2 className="steps__title">4. Create the product</h2>
          <div className="row">
            <button
              type="button"
              className="btn btn--primary"
              data-testid="create-product"
              disabled={running}
              onClick={submit}
            >
              {running ? INTAKE_PHASE_LABELS[intake.state.phase] : "Create product"}
            </button>
            {showProblems && problems.length > 0 ? (
              <span className="faint">
                {problems.length}{" "}
                {problems.length === 1 ? "thing still needs" : "things still need"} an
                answer.
              </span>
            ) : null}
          </div>
        </li>
      ) : null}

      {intake.state.phase !== "idle" ? (
        <li className="steps__item">
          {/* "Done" was the word that misled: what is done is the intake, and
              the product it produced is a draft nobody can buy yet. */}
          <h2 className="steps__title">
            {done ? "Draft created" : "Creating"}
          </h2>
          <IntakeRunPanel state={intake.state} done={done} />
        </li>
      ) : null}
    </>
  );
}

/**
 * The colours a draft-first product is created with.
 *
 * A lens is created with exactly the one approved colour the administrator
 * chose — that is the product. An accessory with a real choice is created with
 * the variants that were actually described; one without is created with a
 * single structural variant carrying its stock, named after the product itself
 * rather than "Default", because "Default" is a colour nobody sells and naming
 * it would put an invented choice in front of a customer.
 */
function variantsForCreate(
  details: NonGlassesDetails,
  family: Exclude<ProductFamily, "glasses">,
): DraftVariantBody[] {
  if (family === "contact_lenses") {
    const color = lensColorForKey(details.lensColorKey);
    if (!color) return [];
    return [
      {
        name: color.name,
        swatch_hex: color.swatch_hex,
        stock_quantity: parseStock(details.stock) ?? 0,
        size_options: [],
      },
    ];
  }

  if (details.variants.length > 0) {
    return details.variants.map((variant, index) => ({
      name: variant.name.trim(),
      swatch_hex: variant.swatch_hex.trim(),
      stock_quantity: parseStock(variant.stock) ?? 0,
      size_options: [],
      display_order: index,
    }));
  }

  // No swatch_hex at all. The backend now stores that as null — "this variant
  // has no colour" — instead of defaulting it to black, so omitting the field
  // is the whole of the fix. Sending '#000000' here put a colour the shop does
  // not sell onto a product that has none, and hiding the row from the
  // administrator never made the stored value true.
  return [
    {
      name: details.name.trim(),
      stock_quantity: parseStock(details.stock) ?? 0,
      size_options: [],
    },
  ];
}

/** What actually happened, photograph by photograph. Same vocabulary as the
 *  glasses import run: a product created two ways is never described two ways. */
/**
 * The intake while it runs, and what the operator is left holding when it ends.
 *
 * What it ends with is a DRAFT. The photographs are stored and prepared, the
 * product exists, and nothing about it is visible to a customer until someone
 * validates and publishes it from the product's own page. That last sentence
 * was the one missing: the panel said "Complete", which is true of the intake
 * and reads as true of the product, and an administrator went looking for the
 * product in the shop. Publication is not automatic and is not started here.
 */
function IntakeRunPanel({
  state,
  done,
}: {
  state: ReturnType<typeof useProductIntake>["state"];
  done: boolean;
}) {
  return (
    <div className="stack" data-testid="intake-run">
      <p>
        <strong>{INTAKE_PHASE_LABELS[state.phase]}</strong>
      </p>
      {done ? (
        <div className="notice" role="status" data-testid="intake-draft-notice">
          <p>Photographs uploaded successfully.</p>
          <p>
            <strong>This product is not published yet.</strong> Open the product
            to validate and publish it.
          </p>
        </div>
      ) : null}
      {state.photos.length > 0 ? (
        <ul className="fileList">
          {state.photos.map((photo) => (
            <li key={`${photo.variantIndex}:${photo.role}`}>
              {photo.variantName ? (
                <span className="muted">{photo.variantName} · </span>
              ) : null}
              <span>{photo.label}</span> <span className="mono">{photo.role}</span>{" "}
              <span className="muted">{photo.status}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {state.message ? (
        <div className="notice notice--error" role="alert">
          {state.message}
        </div>
      ) : null}
      {state.error ? <ErrorNotice error={state.error} /> : null}
      {state.productId ? (
        <p className="row">
          <Link
            className="btn btn--primary"
            to={`/products/${state.productId}`}
            data-testid="open-created-product"
          >
            Open product
          </Link>
        </p>
      ) : null}
    </div>
  );
}

/** The frame intake, exactly as it was. Lifted into its own component only so
 *  the page above can show one family's steps or the other's; not one line of
 *  what it does has changed. */
function GlassesIntakeSteps({
  classification,
  palette,
}: {
  classification: Classification;
  palette: PaletteColor[];
}) {
  const [picked, setPicked] = useState<ReturnType<
    typeof filesFromDirectoryInput
  > | null>(null);
  const [dragging, setDragging] = useState(false);
  const [assignments, setAssignments] = useState<ColorAssignments>({});
  // Views the operator assigned by hand for files the parser would not place.
  const [roleOverrides, setRoleOverrides] = useState<RoleOverrides>({});
  const [details, setDetails] = useState<GlassesDetails | null>(null);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [reviewing, setReviewing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // "This frame already exists in another size." Held here between finding the
  // candidate and the operator answering; nothing is created until they do.
  const [modelPrompt, setModelPrompt] = useState<ModelCandidate | null>(null);
  const [checkingModel, setCheckingModel] = useState(false);
  const pendingImportRef = useRef<ImportInput | null>(null);

  const importer = useGlassesImport();

  const classified = isClassificationComplete(classification);

  // Re-parsed whenever the palette changes, so a colour the operator has just
  // added stops reading as unknown without them re-choosing the folder.
  const parse: GlassesFolderParse | null =
    picked && picked.length > 0 ? parseGlassesFolder(picked, palette) : null;

  /**
   * The folder once the operator's view assignments are applied.
   *
   * A folder the parser refused is not necessarily a folder that cannot be
   * imported — it is often one where a filename did not say which view it was.
   * Everything downstream reads this rather than the raw parse, so a corrected
   * folder behaves exactly like one that was unambiguous to begin with.
   */
  const folder = useMemo(
    () => (parse ? resolveGlassesFolder(parse, roleOverrides) : null),
    [parse, roleOverrides],
  );
  const product = folder?.product ?? null;

  /** The colours whose identity is settled — matched outright, or chosen. */
  const resolved: ResolvedColor[] = useMemo(() => {
    if (!product) return [];
    return product.colors.flatMap((color) => {
      const chosen =
        color.color.kind === "matched"
          ? color.color.color
          : assignments[color.folderName];
      return chosen ? [{ folderName: color.folderName, color: chosen }] : [];
    });
  }, [product, assignments]);

  const allColorsResolved =
    product !== null && resolved.length === product.colors.length;

  const accept = (files: ReturnType<typeof filesFromDirectoryInput>) => {
    setPicked(files.length === 0 ? null : files);
    // A new folder is a new product: nothing decided about the old one carries
    // over, least of all a colour assignment that named a folder now gone, or a
    // view assigned to a file that is no longer in the selection.
    setAssignments({});
    setRoleOverrides({});
    setDetails(null);
    setTouched(new Set());
    setReviewing(false);
  };

  // Created once the folder is clean, seeded with the folder name as a
  // suggestion the operator can overwrite.
  const currentDetails =
    details ?? (product ? emptyDetails(product.rootName) : null);

  const problems =
    currentDetails && product
      ? detailProblems(
          currentDetails,
          resolved.map((color) => color.folderName),
        )
      : [];

  const detailsReady = allColorsResolved && problems.length === 0;
  const importing = importer.state.phase !== "idle";

  const touch = (field: string) =>
    setTouched((current) => new Set(current).add(field));

  /** Show every problem at once — used when Review is pressed too early. */
  const revealAll = () => {
    setTouched(
      new Set([
        "name",
        "productCode",
        "description",
        "size",
        "regular",
        "original",
        "sale",
        ...resolved.map((color) => `stock:${color.folderName}`),
      ]),
    );
  };

  return (
    <>
      {classified ? (
          <li className="steps__item">
            <h2 className="steps__title">2. Choose the product folder</h2>
            <p className="steps__hint">
              One folder for the whole product, with a folder inside it for each
              colour. Each colour folder holds its main, side and angle photographs.
            </p>

            <div
              className={dragging ? "dropZone is-over" : "dropZone"}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                void filesFromDrop(event.dataTransfer).then(accept);
              }}
            >
              <p className="dropZone__headline">Drop one complete product folder here</p>
              <p className="dropZone__or">or</p>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => inputRef.current?.click()}
              >
                Choose Product Folder
              </button>
              <input
                ref={inputRef}
                className="visuallyHidden"
                type="file"
                multiple
                // Directory selection is a non-standard attribute pair that React
                // does not type; both spellings are needed for browser coverage.
                {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
                onChange={(event) => accept(filesFromDirectoryInput(event.target.files))}
                data-testid="folder-input"
              />
            </div>
          </li>
        ) : null}

        {parse ? (
          <li className="steps__item">
            <h2 className="steps__title">3. Check what was found</h2>
            <DetectionReview
              parse={parse}
              palette={palette}
              onAssignmentsChange={setAssignments}
              overrides={roleOverrides}
              onOverridesChange={setRoleOverrides}
            />
          </li>
        ) : null}

        {allColorsResolved && currentDetails && !importing ? (
          <li className="steps__item">
            <h2 className="steps__title">4. Product information</h2>
            <ProductDetailsStep
              details={currentDetails}
              colors={resolved}
              problems={problems}
              touched={touched}
              onTouch={touch}
              onChange={setDetails}
            />
            {!reviewing ? (
              <div className="row">
                <button
                  type="button"
                  className="btn btn--primary"
                  data-testid="review-import"
                  onClick={() => {
                    if (detailsReady) setReviewing(true);
                    else revealAll();
                  }}
                >
                  Review before importing
                </button>
                {!detailsReady && touched.size > 0 ? (
                  <span className="faint">
                    {problems.length}{" "}
                    {problems.length === 1 ? "thing still needs" : "things still need"}{" "}
                    an answer.
                  </span>
                ) : null}
              </div>
            ) : null}
          </li>
        ) : null}

        {reviewing &&
        detailsReady &&
        currentDetails &&
        product &&
        classification.audience &&
        classification.glassesType &&
        classification.frameShape &&
        !importing ? (
          <li className="steps__item">
            <h2 className="steps__title">5. Check and import</h2>
            <ImportSummary
              parse={product}
              colors={resolved}
              details={currentDetails}
              audience={classification.audience}
              glassesType={classification.glassesType}
              frameShape={classification.frameShape}
              onBack={() => setReviewing(false)}
              onEdit={() => setReviewing(false)}
              starting={importing || checkingModel}
              onStart={() => {
                const input: ImportInput = {
                  parse: product,
                  assignments,
                  details: currentDetails,
                  audience: classification.audience!,
                  glassesType: classification.glassesType!,
                  frameShape: classification.frameShape!,
                };
                pendingImportRef.current = input;
                const size = currentDetails.size;
                if (!size) {
                  void importer.start(input);
                  return;
                }
                setCheckingModel(true);
                void resolveModelCandidate(currentDetails.name, size)
                  .then((decision) => {
                    if (decision.kind === "clear") {
                      void importer.start(input);
                      return;
                    }
                    setModelPrompt(decision);
                  })
                  // Discovery is a convenience, not the rule. If the lookup
                  // fails the ordinary flow runs and the BACKEND still refuses
                  // a duplicate slug or a duplicate model+size — so a broken
                  // search costs a helpful question, never correctness.
                  .catch(() => void importer.start(input))
                  .finally(() => setCheckingModel(false));
              }}
            />
          </li>
        ) : null}

        {importing ? (
          <li className="steps__item">
            <h2 className="steps__title">6. Importing</h2>
            <ImportRunPanel
              state={importer.state}
              onRetryFile={(relativePath) => {
                const file = picked?.find((f) => f.relativePath === relativePath);
                if (file) void importer.retryFile(relativePath, file.file);
              }}
            />
            {/* The product exists from this point on. Continuing into the
                workspace is the same destination the Products List reaches, so
                the administrator lands where every other route lands. */}
            {importer.state.productId ? (
              <p className="row">
                <Link
                  className="btn btn--primary"
                  to={`/products/${importer.state.productId}`}
                  data-testid="open-created-product"
                >
                  Open the product
                </Link>
              </p>
            ) : null}
          </li>
        ) : null}

        {/* Same model, a size it does not have yet. The relationship is created
            from the candidate's UUID — the name only found it. */}
        {modelPrompt?.kind === "candidate" ? (
          <ConfirmDialog
            title="Add as another size?"
            confirmLabel={`Add ${SIZE_LABELS[modelPrompt.newSize]}`}
            onCancel={() => setModelPrompt(null)}
            onConfirm={() => {
              const input = pendingImportRef.current;
              const modelOfProductId = modelPrompt.modelProductId;
              setModelPrompt(null);
              if (input) void importer.start({ ...input, modelOfProductId });
            }}
          >
            <p data-testid="model-candidate-prompt">
              <strong>{modelPrompt.modelName}</strong> already exists in{" "}
              {modelPrompt.existingSizes
                .map((size: SizeOption) => SIZE_LABELS[size])
                .join(", ")}
              .
            </p>
            <p>
              Add {SIZE_LABELS[modelPrompt.newSize]} as another size of the same
              model?
            </p>
            <p className="field__hint">
              It becomes its own product, with its own product code and its own
              stock. The shop's app shows one card for the model.
            </p>
          </ConfirmDialog>
        ) : null}

        {/* Same model, same size. Not a grouping question — that entry IS this
            product, and the way forward is to edit it. */}
        {modelPrompt?.kind === "duplicate" ? (
          <ConfirmDialog
            title="This size already exists"
            confirmLabel="Back to product information"
            onCancel={() => setModelPrompt(null)}
            onConfirm={() => {
              setModelPrompt(null);
              setReviewing(false);
            }}
          >
            <p data-testid="model-duplicate-prompt">
              <strong>{modelPrompt.modelName}</strong> already exists in{" "}
              {SIZE_LABELS[modelPrompt.size]}
              {modelPrompt.existingCode ? ` (${modelPrompt.existingCode})` : ""}.
            </p>
            <p>Choose a different size, or edit the product that already exists.</p>
          </ConfirmDialog>
        ) : null}
    </>
  );
}

/* --------------------------------------------------------- edit and resume */

/**
 * How a processing job's status is spoken about.
 *
 * The same words the import run uses, from the same table, so a job watched
 * while adding a product and the same job watched after coming back to it are
 * never described differently.
 */
const JOB_PHASE: Record<ProcessingJobStatus, ImportPhase> = {
  queued: "queued",
  running: "running",
  needs_review: "review_needed",
  completed: "complete",
  failed: "failed",
  cancelled: "cancelled",
};

/** Statuses that mean the import stopped before the product was finished. */
function isUnfinished(status: string): boolean {
  return status === "processing" || status === "needs_review";
}

function ExistingProductWorkspace() {
  const { productId = "" } = useParams();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const detail = useProductDetail(productId);
  const refresh = useProductRefresh(productId);
  const [tab, setTab] = useState<Tab>("details");

  const deepLinked = deepLinkedAction(params.get("action"));

  const product = detail.data?.product ?? null;
  const batchId = product?.source_upload_batch_id ?? null;
  const unfinished = product ? isUnfinished(product.status) : false;

  // Resume: the batch this product came from, and the job the worker is running
  // on it. Read only — nothing here uploads a byte or queues a job, because
  // both already happened. Polling stops the moment the job settles.
  const batch = useQuery({
    queryKey: ["upload-batch", batchId],
    queryFn: ({ signal }) => uploadsApi.batch(batchId!, signal),
    enabled: Boolean(batchId) && unfinished,
  });

  const jobId = batch.data?.processing_job_ids.at(-1) ?? null;
  const job = useQuery({
    queryKey: ["processing-job", jobId],
    queryFn: ({ signal }) => uploadsApi.job(jobId!, signal),
    enabled: Boolean(jobId) && unfinished,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "queued" || status === "running" ? 2000 : false;
    },
  });

  const openDraft = useMutation({
    mutationFn: () => productsApi.openDraft(productId),
    // Returns the existing draft when one is already open, so a double click
    // cannot produce a second draft.
    onSettled: () => refresh(),
  });

  if (detail.isPending) {
    return (
      <Shell title="Product">
        <div className="empty" role="status">
          Loading product…
        </div>
      </Shell>
    );
  }

  if (detail.isError) {
    return (
      <Shell title="Product">
        <div className="stack">
          <ErrorNotice error={detail.error} />
          <p>
            <Link to="/products">Back to products</Link>
          </p>
        </div>
      </Shell>
    );
  }

  const {
    product: loaded,
    live_revision,
    draft_revision,
    available_actions,
    processing_warnings,
  } = detail.data;
  // Two different questions, and both have to be yes. The PRODUCT is editable
  // when a draft is open — the live revision is never edited in place. The
  // ADMINISTRATOR may edit it when they hold the catalogue write capability;
  // without it the same screen is a read-only view of the same facts. Neither
  // is the authorization: every save is refused server-side on its own
  // authority, and this only stops a viewer being offered a form that cannot
  // be submitted.
  const mayWrite = can(CAP.productsWrite);
  const canOpenDraft = available_actions.includes("open_draft") && mayWrite;
  const editable = draft_revision !== null && mayWrite;
  const jobStatus = job.data?.status ?? null;

  // Which contract this product is edited under. Read from the product's own
  // stored category — never from the route that reached this screen, and never
  // from what was chosen while adding something else in another tab. An
  // unrecognised category is null, which is "not classified" and is left alone
  // rather than shown as a frame.
  const family = familyForCategory(draft_revision?.category ?? loaded.category);

  const clearDeepLink = () => {
    const next = new URLSearchParams(params);
    next.delete("action");
    setParams(next, { replace: true });
  };

  return (
    <Shell
      title={
        <span className="row">
          <Link to="/products" className="btn btn--subtle btn--sm">
            ← Products
          </Link>
          <span>{loaded.name}</span>
        </span>
      }
    >
      <div className="stack">
        <header className="panel panel__body workspace__header">
          <div className="stack stack--tight" style={{ minWidth: 260 }}>
            <div className="row">
              <StatusBadge status={loaded.status} />
              <span className="mono">{loaded.slug}</span>
            </div>
            <dl className="workspace__meta">
              <dt>Product version</dt>
              <dd className="num">{loaded.version}</dd>
              <dt>Live revision</dt>
              <dd className="num">
                {live_revision ? live_revision.revision_number : "none"}
              </dd>
              <dt>Draft revision</dt>
              <dd className="num">
                {draft_revision ? draft_revision.revision_number : "none"}
              </dd>
              {live_revision ? (
                <>
                  <dt>Live price</dt>
                  <dd className="num">
                    {formatMoney(live_revision.base_price, live_revision.currency)}
                  </dd>
                </>
              ) : null}
            </dl>
          </div>

          <div className="spacer" />

          <div className="stack stack--tight">
            <div className="row row--wrap">
              {canOpenDraft ? (
                <button
                  type="button"
                  className="btn"
                  disabled={openDraft.isPending}
                  onClick={() => openDraft.mutate()}
                  data-testid="open-draft"
                >
                  {openDraft.isPending ? "Opening…" : "Open draft"}
                </button>
              ) : null}
            </div>
            {/* Every lifecycle transition this application performs happens
                here — including the ones the Products List sends in with
                ?action=, which arrive as a request to open this component's own
                confirmation rather than as a second implementation. */}
            <LifecycleActions
              productId={productId}
              product={loaded}
              live={live_revision}
              draft={draft_revision}
              availableActions={available_actions}
              initialAction={
                deepLinked === "publish" ||
                deepLinked === "publish_changes" ||
                deepLinked === "archive"
                  ? deepLinked
                  : null
              }
              onInitialActionConsumed={clearDeepLink}
            />
          </div>
        </header>

        {openDraft.isError ? <ErrorNotice error={openDraft.error} /> : null}

        {/* Resume. The import that made this product did not finish, so the
            first thing shown is where it actually got to — read back from the
            worker's own job rather than guessed from how long ago it started.
            Nothing on this screen re-uploads or re-queues anything. */}
        {unfinished ? (
          <div className="notice notice--info" data-testid="resume-panel">
            <strong>This product is still being prepared.</strong>{" "}
            {jobStatus ? (
              <>
                Image preparation:{" "}
                <strong>{PHASE_LABELS[JOB_PHASE[jobStatus]]}</strong>.
              </>
            ) : batch.isPending || job.isPending ? (
              "Checking where it got to…"
            ) : (
              "No image-preparation job was found for it."
            )}
            {job.data?.report?.errors?.length ? (
              <ul>
                {job.data.report.errors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            ) : null}
            <p className="muted">
              Your photographs are already stored and the job is already queued —
              coming back here never uploads them again or starts a second job.
            </p>
          </div>
        ) : null}

        {live_revision && draft_revision ? (
          <div className="notice notice--info">
            Customers continue to see revision {live_revision.revision_number}.
            Your edits stay in draft revision {draft_revision.revision_number}
            {" "}until it is published.
          </div>
        ) : null}

        {processing_warnings.length > 0 ? (
          <div className="notice notice--warning">
            <strong>Processing warnings</strong>
            <ul>
              {processing_warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <nav className="workspace__tabs" aria-label="Product sections">
          {(
            [
              ["details", "Details"],
              // Named for what the section actually holds. A lens has one
              // colour, and an accessory that comes one way has none at all —
              // calling either tab "Colors" would promise a choice the product
              // does not offer, which is the same invented colour the panel
              // itself refuses to show.
              ["colors", colorsTabLabel(family, draft_revision ?? live_revision)],
              ["images", "Images"],
              ["validation", "Validation"],
              ["history", "History"],
            ] as [Tab, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={tab === key ? "workspace__tab is-active" : "workspace__tab"}
              aria-current={tab === key ? "page" : undefined}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </nav>

        {tab === "details" ? (
          editable && draft_revision ? (
            <MetadataForm
              productId={productId}
              product={loaded}
              draft={draft_revision}
            />
          ) : (
            <NoDraftNotice canOpenDraft={canOpenDraft} />
          )
        ) : null}

        {tab === "colors" ? (
          draft_revision ? (
            <VariantsPanel
              productId={productId}
              draft={draft_revision}
              editable={editable}
              family={family}
            />
          ) : (
            <NoDraftNotice canOpenDraft={canOpenDraft} />
          )
        ) : null}

        {tab === "images" ? (
          <ImagesPanel
            productId={productId}
            // The draft when one is open, otherwise the live revision shown
            // read-only. Editing is only ever offered against the draft.
            revision={draft_revision ?? live_revision}
            // Photographs are their OWN capability, not the metadata one: the
            // backend gates uploads and reordering on `products.media`, so this
            // asks the same question rather than a nearby one.
            editable={draft_revision !== null && can(CAP.productsMedia)}
            draftVersion={draft_revision?.version ?? null}
            family={family}
          />
        ) : null}

        {tab === "validation" ? (
          <ValidationPanel productId={productId} canValidate={editable} />
        ) : null}

        {tab === "history" ? (
          <HistoryPanel
            productId={productId}
            product={loaded}
            draft={draft_revision}
            active={tab === "history"}
          />
        ) : null}
      </div>
    </Shell>
  );
}

/** What the colours section is called for this family, given what it holds. */
function colorsTabLabel(
  family: ProductFamily | null,
  revision: { color_variants?: { id: string }[] } | null,
): string {
  if (family === "contact_lenses") return "Lens colour";
  if (family === "accessories") {
    return (revision?.color_variants ?? []).length > 1 ? "Variants" : "Stock";
  }
  return "Colors";
}

function NoDraftNotice({ canOpenDraft }: { canOpenDraft: boolean }) {
  return (
    <div className="panel panel__body">
      <p className="muted">
        {canOpenDraft
          ? "This product has no editable draft. Open one to make changes — the published revision stays live and untouched while you edit."
          : "This product cannot be edited in its current state."}
      </p>
    </div>
  );
}
